import {
  readRetargetingCandidateStoreResult,
  RESLINK,
  type RetargetingCandidate,
} from '@marriott/mi-localstorage-utils';
import { logger } from '@marriott/mi-headless-utils';
import { isRecord, resolveImageUrl } from '../slotPropNormalizerUtils';
import {
  RECENTLY_VIEWED_STORAGE_KEY,
  HOMEPAGE_HWS_RLM_USE_CASE,
  RETARGETING_ELIGIBILITY_WINDOW_14_DAYS_MS,
  RETARGETING_RECENTLY_VIEWED_CANDIDATES_INVALID_EVENT,
  RETARGETING_RECENTLY_VIEWED_JSON_INVALID_EVENT,
  RETARGETING_RECENTLY_VIEWED_STORAGE_ERROR_EVENT,
  RETARGETING_RECENTLY_VIEWED_STORAGE_INVALID_EVENT,
  RETARGETING_RESLINK_CANDIDATES_INVALID_EVENT,
  RETARGETING_RESLINK_STORAGE_ACCESS_ERROR_EVENT,
  RETARGETING_RESLINK_STORAGE_INVALID_EVENT,
} from './retargeting.constants';
import {
  type HomepageHwsRlmRetargetingCandidate,
  type HomepageReslinkRetargetingCandidate,
  type HomepageRetargetingCandidate,
  type HomepageRetargetingUseCase,
} from './retargeting.types';

type RecentlyViewedRecord = object;
const { log } = logger({})('retargetingCandidates');

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function safeReadLocalStorage(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    if (typeof window.localStorage === 'undefined') return null;
    return window.localStorage.getItem(key);
  } catch {
    log.warn(RETARGETING_RECENTLY_VIEWED_STORAGE_ERROR_EVENT, { storageKey: key });
    return null;
  }
}

function getString(record: RecentlyViewedRecord, ...keys: string[]): string {
  const source = record as Record<string, unknown>;
  for (const key of keys) {
    const value = source[key];
    if (isNonEmptyString(value)) return value.trim();
  }
  return '';
}

function getFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function toTimestampMs(timestamp: string): number | null {
  const parsed = Date.parse(timestamp);
  return Number.isFinite(parsed) ? parsed : null;
}

function selectRecentlyViewedImage(record: RecentlyViewedRecord): string {
  const imageSrc = getString(record, 'imageSrc');
  const imageUrl = getString(record, 'imageUrl');
  return imageSrc && !/\.tiff?(?=$|[?#:])/i.test(imageSrc) ? imageSrc : imageUrl;
}

export function resolveNowMs(now: Date | string | number): number {
  const candidate = now instanceof Date ? now.getTime() : new Date(now).getTime();
  return Number.isFinite(candidate) ? candidate : Date.now();
}

function readRecentlyViewedRecords(): { records: RecentlyViewedRecord[]; totalCandidateCount: number } {
  const raw = safeReadLocalStorage(RECENTLY_VIEWED_STORAGE_KEY);
  if (!raw) return { records: [], totalCandidateCount: 0 };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || !Array.isArray(parsed['recentlyViewedProperties'])) {
      log.warn(RETARGETING_RECENTLY_VIEWED_STORAGE_INVALID_EVENT, {
        storageKey: RECENTLY_VIEWED_STORAGE_KEY,
      });
      return { records: [], totalCandidateCount: 0 };
    }
    const entries = parsed['recentlyViewedProperties'];
    return { records: entries.filter(isRecord), totalCandidateCount: entries.length };
  } catch {
    log.warn(RETARGETING_RECENTLY_VIEWED_JSON_INVALID_EVENT, {
      storageKey: RECENTLY_VIEWED_STORAGE_KEY,
    });
    return { records: [], totalCandidateCount: 0 };
  }
}

export function normalizeHwsRlmCandidate(record: RecentlyViewedRecord): HomepageHwsRlmRetargetingCandidate | null {
  const displayName = getString(record, 'name');
  const marshaCode = getString(record, 'marshaCode').toLowerCase();
  const timestamp = getString(record, 'createdAt');
  if (!displayName || !marshaCode || !timestamp) return null;
  const state = getString(record, 'state', 'stateProvince');
  const country = getString(record, 'country');
  const source = record as Record<string, unknown>;
  const latitude = getFiniteNumber(source['latitude']);
  const longitude = getFiniteNumber(source['longitude']);

  return {
    useCase: HOMEPAGE_HWS_RLM_USE_CASE,
    displayName,
    imageUrl: resolveImageUrl(selectRecentlyViewedImage(record)),
    targetUrl: `/reservation/availabilitySearch.mi?isSearch=false&propertyCode=${marshaCode}&flexibleDateSearch=true`,
    marshaCode,
    brandCode: getString(record, 'marshaBrandCode', 'brandCode'),
    timestamp,
    eyebrow: [state, country].filter(Boolean).join(', '),
    ...(latitude !== undefined ? { latitude } : {}),
    ...(longitude !== undefined ? { longitude } : {}),
  };
}

export function normalizeHwsRlmCandidates(_now: Date | string | number): HomepageHwsRlmRetargetingCandidate[] {
  const result = readRecentlyViewedRecords();
  const candidates = result.records
    .map(normalizeHwsRlmCandidate)
    .filter((value): value is HomepageHwsRlmRetargetingCandidate => value !== null);
  const rejectedCandidateCount = result.totalCandidateCount - candidates.length;
  if (rejectedCandidateCount > 0) {
    log.warn(RETARGETING_RECENTLY_VIEWED_CANDIDATES_INVALID_EVENT, {
      storageKey: RECENTLY_VIEWED_STORAGE_KEY,
      totalCandidateCount: result.totalCandidateCount,
      rejectedCandidateCount,
    });
  }
  return candidates;
}

export function normalizeReslinkCandidate(candidate: RetargetingCandidate): HomepageReslinkRetargetingCandidate | null {
  if (candidate.useCase !== RESLINK) return null;
  const displayName = candidate.displayName.trim();
  const imageUrl = candidate.imageUrl.trim();
  const targetUrl = candidate.targetUrl.trim();
  const timestamp = candidate.timestamp.trim();
  const marshaCode = candidate.marshaCode.map(code => code.trim().toLowerCase()).filter(Boolean);
  if (!displayName || !imageUrl || !targetUrl || !timestamp || !marshaCode.length) return null;
  const eventExpirationDate = candidate.eventExpirationDate?.trim();

  return {
    useCase: RESLINK,
    displayName,
    imageUrl,
    targetUrl,
    marshaCode,
    timestamp,
    eyebrow: candidate.groupName?.trim() ?? '',
    ...(eventExpirationDate ? { eventExpirationDate } : {}),
    ...(typeof candidate.cancellationFlag === 'boolean' ? { cancellationFlag: candidate.cancellationFlag } : {}),
  };
}

export function normalizeReslinkCandidates(_now: Date | string | number): HomepageReslinkRetargetingCandidate[] {
  const result = readRetargetingCandidateStoreResult();
  if (result.status === 'storage_error') {
    log.warn(RETARGETING_RESLINK_STORAGE_ACCESS_ERROR_EVENT, { status: result.status });
  } else if (result.status === 'invalid') {
    log.warn(RETARGETING_RESLINK_STORAGE_INVALID_EVENT, { status: result.status });
  } else if (result.status === 'success' && result.rejectedCandidateCount > 0) {
    log.warn(RETARGETING_RESLINK_CANDIDATES_INVALID_EVENT, {
      totalCandidateCount: result.totalCandidateCount,
      rejectedCandidateCount: result.rejectedCandidateCount,
    });
  }

  return result.candidates
    .map(normalizeReslinkCandidate)
    .filter((value): value is HomepageReslinkRetargetingCandidate => value !== null);
}

function isEligible(
  candidate: HomepageRetargetingCandidate,
  nowMs: number,
  eligibleUseCases: Set<HomepageRetargetingUseCase>
): boolean {
  if (!eligibleUseCases.has(candidate.useCase)) return false;
  const timestampMs = toTimestampMs(candidate.timestamp);
  if (timestampMs === null || timestampMs > nowMs || nowMs - timestampMs > RETARGETING_ELIGIBILITY_WINDOW_14_DAYS_MS) {
    return false;
  }
  if (candidate.useCase === RESLINK) {
    if (candidate.cancellationFlag === true) return false;
    if (candidate.eventExpirationDate) {
      const expirationMs = toTimestampMs(candidate.eventExpirationDate);
      if (expirationMs === null || expirationMs < nowMs) return false;
    }
  }
  return true;
}

export function selectHomepageRetargetingCandidate({
  now,
  eligibleUseCases,
  candidates,
}: {
  now: Date | string | number;
  eligibleUseCases: readonly HomepageRetargetingUseCase[];
  candidates: HomepageRetargetingCandidate[];
}): HomepageRetargetingCandidate | null {
  const eligibleSet = new Set(eligibleUseCases);
  const sortedCandidates = candidates
    .filter(candidate => isEligible(candidate, resolveNowMs(now), eligibleSet))
    .sort((a, b) => {
      const timestampDiff = (toTimestampMs(b.timestamp) ?? 0) - (toTimestampMs(a.timestamp) ?? 0);
      if (timestampDiff) return timestampDiff;
      if (a.useCase !== b.useCase) return a.useCase === RESLINK ? 1 : -1;
      return 0;
    });
  const newest = sortedCandidates[0];
  if (!newest || newest.useCase === RESLINK) return newest ?? null;
  return (
    sortedCandidates.find(
      (value): value is HomepageReslinkRetargetingCandidate =>
        value.useCase === RESLINK && value.marshaCode.includes(newest.marshaCode)
    ) ?? newest
  );
}
